import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Badge,
  Card,
  Grid,
  Group,
  Loader,
  Progress,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { IconAlertCircle, IconCoin, IconUsers, IconActivity } from '@tabler/icons-react'
import { useArchbaseRemoteServiceApi } from '@archbase/data'
import { API_TYPE } from '@ioc/IOCTypes'
import type { DoclenzUsageService } from '@services/DoclenzUsageService'
import { monthlyQuotaPercent, type TenantUsageDto } from '@domain/usage/UsageDto'

/** Consumo por tenant, sempre contra a cota — número solto não diz se está perto do limite. */
export function HomeView() {
  const usageApi = useArchbaseRemoteServiceApi<DoclenzUsageService>(API_TYPE.DoclenzUsage)
  const [usage, setUsage] = useState<TenantUsageDto[]>([])
  const [health, setHealth] = useState<{ status: string; workers: Record<string, string> } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [usageResponse, healthResponse] = await Promise.all([
        usageApi.findAll(),
        usageApi.health().catch(() => null),
      ])
      setUsage(usageResponse.usage || [])
      setHealth(healthResponse as any)
    } catch (err: any) {
      setError(err?.message || 'Não foi possível carregar o consumo.')
    } finally {
      setLoading(false)
    }
  }, [usageApi])

  useEffect(() => {
    load()
  }, [load])

  const totalUps = usage.reduce((sum, row) => sum + (row.ups_total || 0), 0)
  const monthUps = usage.reduce((sum, row) => sum + (row.ups_month || 0), 0)

  if (loading) {
    return (
      <Group justify="center" p="xl">
        <Loader />
      </Group>
    )
  }

  return (
    <Stack p="md" gap="lg">
      <Title order={3}>Visão geral</Title>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <Grid>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <StatCard icon={<IconUsers size={20} />} label="Tenants" value={String(usage.length)} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <StatCard icon={<IconCoin size={20} />} label="UPs no mês" value={monthUps.toLocaleString('pt-BR')} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <StatCard
            icon={<IconActivity size={20} />}
            label="API"
            value={health?.status === 'ok' ? 'no ar' : 'indisponível'}
            color={health?.status === 'ok' ? 'green' : 'red'}
          />
        </Grid.Col>
      </Grid>

      <Card withBorder radius="md" p="md">
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={600}>Consumo por tenant</Text>
            <Text size="sm" c="dimmed">
              {totalUps.toLocaleString('pt-BR')} UPs acumuladas
            </Text>
          </Group>

          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tenant</Table.Th>
                <Table.Th>Produto</Table.Th>
                <Table.Th ta="right">UPs no mês</Table.Th>
                <Table.Th ta="right">Total</Table.Th>
                <Table.Th w={200}>Cota mensal</Table.Th>
                <Table.Th>Situação</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {usage.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text size="sm" c="dimmed" ta="center" py="md">
                      Nenhum consumo registrado ainda.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {usage.map((row) => {
                const percent = monthlyQuotaPercent(row)
                return (
                  <Table.Tr key={row.tenant_id}>
                    <Table.Td>
                      <Text fw={500}>{row.tenant_id}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c={row.product ? undefined : 'dimmed'}>
                        {row.product || '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">{row.ups_month.toLocaleString('pt-BR')}</Table.Td>
                    <Table.Td ta="right">{row.ups_total.toLocaleString('pt-BR')}</Table.Td>
                    <Table.Td>
                      {percent === null ? (
                        <Text size="xs" c="dimmed">
                          sem teto
                        </Text>
                      ) : (
                        <Tooltip label={`${row.ups_month} de ${row.ups_per_month} UPs`}>
                          <Progress
                            value={percent}
                            color={percent >= 90 ? 'red' : percent >= 70 ? 'yellow' : 'blue'}
                          />
                        </Tooltip>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status={row.status} />
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Stack>
      </Card>
    </Stack>
  )
}

function StatCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode
  label: string
  value: string
  color?: string
}) {
  return (
    <Card withBorder radius="md" p="md">
      <Group gap="sm">
        {icon}
        <Stack gap={0}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            {label}
          </Text>
          <Text size="xl" fw={700} c={color}>
            {value}
          </Text>
        </Stack>
      </Group>
    </Card>
  )
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'active') return <Badge color="green" variant="light">ativa</Badge>
  if (status === 'no-key') {
    return (
      <Tooltip label="Há consumo registrado, mas nenhuma chave viva para este tenant">
        <Badge color="gray" variant="light">sem chave</Badge>
      </Tooltip>
    )
  }
  return <Badge color="orange" variant="light">{status}</Badge>
}
